import { useState, useEffect } from 'react'
import { AuthService } from '../services/auth'
import PasswordChangeDialog from './PasswordChangeDialog'
import TermsDialog from './TermsDialog'

interface FirstTimeUserDialogProps {
  isOpen: boolean
  onComplete: () => void
}

const FirstTimeUserDialog = ({ isOpen, onComplete }: FirstTimeUserDialogProps) => {
  const [nickname, setNickname] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [showTermsDialog, setShowTermsDialog] = useState(false)
  const [isOAuthUser, setIsOAuthUser] = useState(false)

  // Check if user is OAuth user on mount
  useEffect(() => {
    const checkOAuth = async () => {
      const isOAuth = await AuthService.isOAuthUser()
      setIsOAuthUser(isOAuth)
    }
    if (isOpen) {
      checkOAuth()
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!termsAccepted) {
      setError('利用規約に同意してください')
      return
    }

    try {
      setIsLoading(true)
      setError('')

      // Update user profile with nickname and terms acceptance
      await AuthService.completeFirstTimeOnboarding(nickname.trim() || undefined)

      // Call completion callback
      onComplete()
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      setError('設定の保存に失敗しました。もう一度お試しください。')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null


  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">初回設定</h2>
            <p className="text-sm text-gray-600 mt-1">
              MiraPASSへようこそ！
            </p>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="px-6 py-4 space-y-6 overflow-y-auto">

              {/* Password Change Section - Only for non-OAuth users */}
              {!isOAuthUser && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    初期パスワード変更(任意)
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowPasswordDialog(true)}
                    className="text-blue-600 underline hover:text-blue-800 mb-1"
                  >
                    こちら
                  </button>
                  <p className="text-xs text-gray-500">
                    ※後ほどマイページから変更できます
                  </p>
                </div>
              )}

              {/* Nickname Section */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  ニックネーム(任意)
                </h3>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="表示名として使用されます"
                  maxLength={50}
                />
                <p className="text-xs text-gray-500 mt-1">
                  ※後ほどマイページから設定できます
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="text-red-800 text-sm">{error}</div>
                </div>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="terms-accept"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mt-0.5"
                />
                <label htmlFor="terms-accept" className="ml-2 text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowTermsDialog(true)}
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    利用規約・プライバシーポリシー
                  </button>
                  <span>に同意の上、利用します</span>
                  <span className="text-red-500">*</span>
                </label>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="submit"
                disabled={isLoading || !termsAccepted}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? '設定中...' : 'MiraPASSを始める'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Nested Dialogs */}
      <PasswordChangeDialog
        isOpen={showPasswordDialog}
        onSuccess={() => setShowPasswordDialog(false)}
        onError={(error) => {
          console.error('Password change error:', error)
          setError(error)
        }}
        onClose={() => setShowPasswordDialog(false)}
        isMandatory={false}
      />

      <TermsDialog
        isOpen={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />
    </>
  )
}

export default FirstTimeUserDialog
